import _ from 'lodash'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import {
  Card,
  CardTitle,
  CardText
} from 'react-md'
import { withRouter } from 'react-router-dom'

import { ArtworkStatus } from 'api/enums'
import config from 'config'
import {
  getArtworks,
  getArtworkPage,
  getArtworkRowsPerPage,
  getArtworkCount
} from 'redux/reducers'
import * as actions from 'redux/actions'
import * as routes from 'routes'

import ArtworkList from './ArtworkList'
import ArtworkListActions from './ArtworkListActions'
import DeleteDialog from './DeleteDialog'
import UnpublishDialog from './UnpublishDialog'

const RootElement = ({ noCard, children }) =>
  noCard ? <div>{ children }</div> : <Card tableCard>{ children }</Card>

class Artworks extends Component {
  constructor (props) {
    super(props)

    this.state = {
      count: 0,
      selectedRows: [],
      deleteDialogVisible: false,
      unpublishDialogVisible: false
    }

    // utils
    this.refresh = this.refresh.bind(this)
    this.resetPagination = this.resetPagination.bind(this)
    this.getArtworkFromRow = this.getArtworkFromRow.bind(this)

    // event handlers
    this.handleCancelDelete = this.handleCancelDelete.bind(this)
    this.handleCancelUnpublish = this.handleCancelUnpublish.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.handleConfirmDelete = this.handleConfirmDelete.bind(this)
    this.handleConfirmUnpublish = this.handleConfirmUnpublish.bind(this)
    this.handleDeleteClick = this.handleDeleteClick.bind(this)
    this.handlePagination = this.handlePagination.bind(this)
    this.handlePublishClick = this.handlePublishClick.bind(this)
    this.handleRefreshClick = this.handleRefreshClick.bind(this)
    this.handleUnpublishClick = this.handleUnpublishClick.bind(this)
    this.handleRowToggle = this.handleRowToggle.bind(this)
  }

  componentWillMount () {
    const {
      loadArtworks,
      artworkPageChange,
      artworkRowsPerPageChange,
      artworkStatus
    } = this.props

    // initializes pagination on the first page
    artworkPageChange(1)
    artworkRowsPerPageChange(config.pagination.defaultRowsPerPage)

    // refresh artworks from the current page
    loadArtworks(0, config.pagination.defaultRowsPerPage, artworkStatus)
  }

  refresh () {
    const { artworkPage, artworkRowsPerPage } = this.props
    const start = (artworkPage - 1) * artworkRowsPerPage
    this.handlePagination(start, artworkRowsPerPage)
  }

  resetPagination () {
    this.setState({ count: 0, selectedRows: [] })
  }

  getArtworkFromRow (row) {
    const { artworks, artworkPage, artworkRowsPerPage } = this.props
    return artworks[row + (artworkPage - 1) * artworkRowsPerPage]
  }

  handleCancelDelete () {
    this.setState({ deleteDialogVisible: false })
  }

  handleCancelUnpublish () {
    this.setState({ unpublishDialogVisible: false })
  }

  handleClick (artworkId) {
    const { history, artworkClickRoute } = this.props
    const route = artworkClickRoute || routes.artwork
    history.push(route(artworkId))
  }

  async handleConfirmDelete () {
    const { selectedRows } = this.state
    const { deleteArtwork } = this.props

    await Promise.all(selectedRows.map(deleteArtwork))

    this.setState({
      deleteDialogVisible: false
    })

    this.refresh()
  }

  async handleConfirmUnpublish () {
    const { selectedRows } = this.state
    const { unpublishArtwork, loadArtworkCount } = this.props

    await Promise.all(selectedRows.map(unpublishArtwork))
    await loadArtworkCount(ArtworkStatus.PUBLISHED)

    this.setState({
      unpublishDialogVisible: false
    })

    this.resetPagination()
  }

  handleDeleteClick () {
    this.setState({
      deleteDialogVisible: true
    })
  }

  async handlePagination (start, rowsPerPage) {
    const {
      loadArtworks,
      artworkPageChange,
      artworkRowsPerPageChange,
      artworkStatus
    } = this.props

    this.resetPagination()

    // refresh artworks from the next page
    await loadArtworks(start, rowsPerPage, artworkStatus)

    // updates pagination
    artworkPageChange(Math.floor(start / rowsPerPage) + 1)
    artworkRowsPerPageChange(rowsPerPage)
  }

  async handlePublishClick () {
    const { selectedRows } = this.state
    const { publishArtwork, loadArtworkCount } = this.props

    await Promise.all(selectedRows.map(publishArtwork))
    await loadArtworkCount(ArtworkStatus.PENDING)
    await loadArtworkCount(ArtworkStatus.PUBLISHED)

    this.resetPagination()
  }

  handleRefreshClick () {
    this.refresh()
  }

  handleRowToggle (row, toggled, count) {
    const filter = toggled ? _.concat : _.without
    this.setState((prevState) => {
      const { selectedRows } = prevState
      const artworkId = Number(this.getArtworkFromRow(row - 1).id)
      const filteredRows = filter(selectedRows, artworkId)
      return { count, selectedRows: filteredRows }
    })
  }

  handleUnpublishClick () {
    this.setState({
      unpublishDialogVisible: true
    })
  }

  render () {
    let {
      artworks,
      artworkPage,
      artworkRowsPerPage,
      artworkCount,
      baseId,
      disableDelete,
      enablePublish,
      enableUnpublish,
      noCard,
      title,
      noArtworksMessage
    } = this.props

    const { count, deleteDialogVisible, unpublishDialogVisible } = this.state

    const start = (artworkPage - 1) * artworkRowsPerPage
    const end = start + artworkRowsPerPage

    title = title || 'My Artworks'
    noArtworksMessage = noArtworksMessage || 'You currently have no artworks.'

    if (!artworkCount) {
      return (
        <Card>
          <CardTitle title={title} />
          <CardText>{ noArtworksMessage }</CardText>
        </Card>
      )
    }

    return (
      <RootElement noCard={noCard}>
        <DeleteDialog
          visible={deleteDialogVisible}
          onCancelDelete={this.handleCancelDelete}
          onConfirmDelete={this.handleConfirmDelete}
        />
        <UnpublishDialog
          visible={unpublishDialogVisible}
          onCancelUnpublish={this.handleCancelUnpublish}
          onConfirmUnpublish={this.handleConfirmUnpublish}
        />
        <ArtworkListActions
          count={count}
          title={title}
          enableDelete={!disableDelete}
          enablePublish={enablePublish}
          enableUnpublish={enableUnpublish}
          onDeleteClick={this.handleDeleteClick}
          onPublishClick={this.handlePublishClick}
          onRefreshClick={this.handleRefreshClick}
          onUnpublishClick={this.handleUnpublishClick}
        />
        <ArtworkList
          baseId={baseId}
          data={artworks.slice(start, end)}
          page={artworkPage}
          rowsPerPage={artworkRowsPerPage}
          count={artworkCount}
          handleClick={this.handleClick}
          handlePagination={this.handlePagination}
          onRowToggle={this.handleRowToggle}
          onTabChange={this.resetPagination}
        />
      </RootElement>
    )
  }
}

Artworks.propTypes = {
  artworks: PropTypes.array.isRequired
}

const mapStateToProps = (state, { artworkStatus }) => ({
  artworks: getArtworks(state, artworkStatus),
  artworkPage: getArtworkPage(state),
  artworkRowsPerPage: getArtworkRowsPerPage(state),
  artworkCount: getArtworkCount(state, artworkStatus)
})

export default withRouter(connect(mapStateToProps, actions)(Artworks))



// WEBPACK FOOTER //
// ./src/containers/Artworks/index.js